import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api.js";
import Drawer from "../../components/Drawer.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type ConnectionsData = Awaited<ReturnType<typeof api.platformConnections>>;
type Connection = ConnectionsData["connections"][number];

function authPill(mode: Connection["authMode"]) {
  switch (mode) {
    case "token":         return <span className="pill pill-info">PAT</span>;
    case "basic":         return <span className="pill pill-warn">Basic</span>;
    case "dhis2-session": return <span className="pill pill-info">Session</span>;
    case "none":          return <span className="pill pill-muted">None</span>;
    default:              return <span className="pill pill-muted">{String(mode)}</span>;
  }
}

type RotateForm = { environment: string; serviceName: string; kind: "dhis2-pat" | "dhis2-basic"; secretValue: string };

export default function AdminInstances() {
  const [data, setData] = useState<ConnectionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [healthFilter, setHealthFilter] = useState<"all" | "healthy" | "disabled" | "missing-secret">("all");
  const [workspaceFilter, setWorkspaceFilter] = useState<string>("all");
  const [rotateForm, setRotateForm] = useState<RotateForm | null>(null);
  const [rotating, setRotating] = useState(false);
  const [busyEnv, setBusyEnv] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.platformConnections());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (c: Connection) => {
    if (!window.confirm(`Delete DHIS2 instance "${c.serviceName}" (env "${c.environment}")? This removes the profile entirely.`)) return;
    setBusyEnv(c.environment);
    setError(null);
    try {
      await api.deleteServiceProfile(c.environment);
      setBanner(`Deleted instance ${c.environment}.`);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyEnv(null);
    }
  };

  const onResync = async (c: Connection) => {
    if (!c.workspaceId) {
      setError(`Cannot resync: instance "${c.environment}" is not bound to a workspace.`);
      return;
    }
    const confirmMsg = [
      `Reset & resync DHIS2 instance "${c.serviceName}" (env "${c.environment}")?`,
      "",
      "This will WIPE all synced data from this instance for the workspace:",
      "  • tracker events, enrollments, tracked entities",
      "  • submitted aggregate facts",
      "  • tally definitions and bucket counts (for this instance)",
      "",
      "Sync watermarks will be cleared so the next sync re-fetches everything from scratch.",
      "Tracker-metadata-sync and aggregate-metadata-sync jobs will be queued immediately."
    ].join("\n");
    if (!window.confirm(confirmMsg)) return;
    setBusyEnv(c.environment);
    setError(null);
    try {
      const result = await api.resyncDhis2Instance(c.workspaceId, c.environment);
      const total =
        result.cleared.trackerEvents +
        result.cleared.trackerEnrollments +
        result.cleared.trackedEntities +
        result.cleared.submittedAggregateFacts;
      setBanner(
        `Reset ${c.environment}: cleared ${total} synced records ` +
        `(${result.cleared.tallyDefinitions} defs, ${result.cleared.tallyBuckets} buckets, ` +
        `${result.watermarksCleared} watermarks). Queued ${result.queuedJobIds.length} sync jobs.`
      );
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyEnv(null);
    }
  };

  const onRotateSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!rotateForm) return;
    setRotating(true);
    setError(null);
    try {
      await api.rotateServiceProfileSecret(rotateForm.environment, rotateForm.secretValue, rotateForm.kind);
      setBanner(`Rotated secret for ${rotateForm.environment}.`);
      setRotateForm(null);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setRotating(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const connections = data?.connections ?? [];
  const summary = data?.summary ?? { total: 0, healthy: 0, disabled: 0, missingSecret: 0 };

  const workspaces = useMemo(
    () => Array.from(new Set(connections.map((c) => c.workspaceName ?? "(unassigned)"))).sort(),
    [connections]
  );

  const filtered = useMemo(() => {
    return connections.filter((c) => {
      if (healthFilter === "healthy" && !(c.enabled && c.secretConfigured)) return false;
      if (healthFilter === "disabled" && c.enabled) return false;
      if (healthFilter === "missing-secret" && c.secretConfigured) return false;
      if (workspaceFilter !== "all" && (c.workspaceName ?? "(unassigned)") !== workspaceFilter) return false;
      return true;
    });
  }, [connections, healthFilter, workspaceFilter]);

  const t = useTableState(filtered, {
    searchFields: ["workspaceName", "serviceName", "environment", "baseUrl", "authMode"],
    initialPageSize: 25
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>DHIS2 instances</h1>
          <p>All DHIS2 service profiles registered across the platform — the connections that ingestion, replay, and forwarding jobs target. Secret values are never exposed; only configuration status.</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => void refresh()} disabled={loading}>Refresh</button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}
      {banner && <div className="alert alert-ok">{banner}</div>}

      <section className="section">
        <div className="stat-grid">
          <div className="stat"><div className="stat-label">Total</div><div className="stat-value">{summary.total}</div></div>
          <div className="stat"><div className="stat-label">Healthy</div><div className="stat-value">{summary.healthy}</div></div>
          <div className="stat"><div className="stat-label">Disabled</div><div className="stat-value">{summary.disabled}</div></div>
          <div className="stat"><div className="stat-label">Missing secret</div><div className="stat-value">{summary.missingSecret}</div></div>
        </div>
      </section>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label className="muted" style={{ fontSize: 12 }}>Health</label>
        <select value={healthFilter} onChange={(e) => setHealthFilter(e.target.value as typeof healthFilter)}>
          <option value="all">All</option>
          <option value="healthy">Healthy only</option>
          <option value="disabled">Disabled</option>
          <option value="missing-secret">Missing secret</option>
        </select>
        <label className="muted" style={{ fontSize: 12, marginLeft: 12 }}>Workspace</label>
        <select value={workspaceFilter} onChange={(e) => setWorkspaceFilter(e.target.value)}>
          <option value="all">All workspaces ({workspaces.length})</option>
          {workspaces.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
      </div>

      {loading && connections.length === 0 ? (
        <SkeletonTable rows={5} columns={4} />
      ) : connections.length === 0 ? (
        <div className="empty">
          <p><strong>No DHIS2 service profiles registered yet.</strong></p>
          <p className="muted">
            The platform pulls metadata, aggregate facts, and tracker events from DHIS2 instances declared here.
            Until at least one enabled profile exists with credentials, ingestion jobs will fail with <code>fetch failed</code>.
          </p>
          <p className="muted">
            Profiles are added via <Link to="/admin/settings">Platform settings</Link> or by API
            (<code>PUT /api/settings</code> with a <code>serviceProfiles</code> array).
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          <p>No DHIS2 instances match the current filters.</p>
        </div>
      ) : (
        <div className="table-scroll">
          <TableToolbar
            hideExport
            search={t.search}
            setSearch={t.setSearch}
            page={t.page}
            setPage={t.setPage}
            pageSize={t.pageSize}
            setPageSize={t.setPageSize}
            totalPages={t.totalPages}
            totalRows={t.totalRows}
            searchPlaceholder="Search instances…"
          />
          <table className="data-table">
            <thead>
              <tr>
                <th>Workspace</th>
                <th>Service / environment</th>
                <th>Base URL</th>
                <th>Auth</th>
                <th>Enabled</th>
                <th>Secret</th>
                <th>Last successful</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {t.paged.map((c) => {
                const busy = busyEnv === c.environment;
                const supportsRotate = c.authMode === "token" || c.authMode === "basic";
                return (
                  <tr key={`${c.workspaceId ?? "_"}-${c.environment}`}>
                    <td>{c.workspaceName ?? <span className="soft">(unassigned)</span>}</td>
                    <td>
                      <strong>{c.serviceName}</strong>
                      <div className="soft" style={{ fontSize: 12 }}>{c.environment}</div>
                    </td>
                    <td className="soft"><code>{c.baseUrl || "—"}</code></td>
                    <td>{authPill(c.authMode as Connection["authMode"])}</td>
                    <td>{c.enabled ? <span className="pill pill-ok">Enabled</span> : <span className="pill pill-muted">Disabled</span>}</td>
                    <td>
                      {c.secretConfigured
                        ? <span className="pill pill-ok">Configured</span>
                        : c.authMode === "none"
                        ? <span className="pill pill-muted">N/A</span>
                        : <span className="pill pill-err">Missing</span>}
                    </td>
                    <td className="soft">{c.lastSuccessfulConnection ? new Date(c.lastSuccessfulConnection).toLocaleString() : "—"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {supportsRotate && (
                          <button
                            className="btn btn-sm"
                            type="button"
                            disabled={busy}
                            onClick={() => setRotateForm({
                              environment: c.environment,
                              serviceName: c.serviceName,
                              kind: c.authMode === "basic" ? "dhis2-basic" : "dhis2-pat",
                              secretValue: ""
                            })}
                          >
                            Rotate secret
                          </button>
                        )}
                        <button
                          className="btn btn-sm"
                          type="button"
                          disabled={busy || !c.workspaceId}
                          onClick={() => void onResync(c)}
                          title={c.workspaceId ? "Wipe synced data for this instance and re-sync from scratch" : "Cannot resync — instance is not bound to a workspace"}
                          style={{ color: "#b45309" }}
                        >
                          {busy ? "Working…" : "Reset & resync"}
                        </button>
                        <button
                          className="btn btn-sm"
                          type="button"
                          disabled={busy}
                          onClick={() => void onDelete(c)}
                          style={{ color: "#b91c1c" }}
                        >
                          {busy ? "Working…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Drawer
        open={rotateForm != null}
        title={rotateForm ? `Rotate secret · ${rotateForm.serviceName}` : ""}
        subtitle={rotateForm ? `Environment: ${rotateForm.environment}` : undefined}
        onClose={() => !rotating && setRotateForm(null)}
        width={520}
        asForm
        onSubmit={onRotateSubmit}
        footer={(
          <>
            <button type="button" className="btn" disabled={rotating} onClick={() => setRotateForm(null)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={rotating}>
              {rotating ? "Rotating…" : "Rotate"}
            </button>
          </>
        )}
      >
        {rotateForm && (
          <div style={{ display: "grid", gap: 12 }}>
            <div className="field">
              <label>Secret kind</label>
              <select
                value={rotateForm.kind}
                onChange={(e) => setRotateForm({ ...rotateForm, kind: e.target.value as RotateForm["kind"] })}
              >
                <option value="dhis2-pat">DHIS2 personal access token</option>
                <option value="dhis2-basic">DHIS2 basic auth (user:pass)</option>
              </select>
            </div>
            <div className="field">
              <label>{rotateForm.kind === "dhis2-basic" ? "username:password" : "Token value"}</label>
              <input
                type="password"
                required
                minLength={4}
                value={rotateForm.secretValue}
                onChange={(e) => setRotateForm({ ...rotateForm, secretValue: e.target.value })}
                placeholder={rotateForm.kind === "dhis2-basic" ? "admin:district" : "d2pat_…"}
              />
              <small className="muted">The new secret replaces the old one immediately. Existing connections retry with the new credential on next call.</small>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
